import React, { useCallback, useEffect, useMemo, useState } from 'react';
import s from './Refollow.module.scss';

const DEFAULT_TIME = '08:00';

function todayIstIso() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

function memberId(m) {
  return Number(m.batch_member_id ?? m.id);
}

function fmtPhone(m) {
  return (m.phone_e164 || m.phone || '').toString().trim() || '—';
}

export default function CreateRefollowModal({
  open,
  onClose,
  selectedMembers,
  selectedCount,
  sourceBatchId,
  onSubmit,
  submitting,
}) {
  const [dateMode, setDateMode] = useState('same');
  const [sameDate, setSameDate] = useState(todayIstIso());
  const [sameTime, setSameTime] = useState(DEFAULT_TIME);
  const [perRow, setPerRow] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const rows = useMemo(
    () => (selectedMembers || []).filter((m) => m.customer_id && memberId(m)),
    [selectedMembers],
  );

  const totalSelected = selectedCount ?? rows.length;

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setPerRow({});
    setDateMode('same');
    setSameDate(todayIstIso());
    setSameTime(DEFAULT_TIME);
  }, [open]);

  const defaultSchedule = useCallback(
    () => ({ date: sameDate, time: sameTime }),
    [sameDate, sameTime],
  );

  const rowSchedule = useCallback(
    (id) => perRow[id] || defaultSchedule(),
    [perRow, defaultSchedule],
  );

  const patchRow = useCallback((id, patch) => {
    setPerRow((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || defaultSchedule()), ...patch },
    }));
  }, [defaultSchedule]);

  if (!open) return null;

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const buildItems = () => rows.map((m) => {
    const mid = memberId(m);
    if (dateMode === 'same') {
      return {
        batch_member_id: mid,
        scheduled_date: sameDate,
        scheduled_time: sameTime,
      };
    }
    const sch = rowSchedule(mid);
    return {
      batch_member_id: mid,
      scheduled_date: sch.date || sameDate,
      scheduled_time: sch.time || DEFAULT_TIME,
    };
  });

  const submit = (publish) => {
    onSubmit({
      source_batch_id: sourceBatchId,
      publish,
      items: buildItems(),
    });
  };

  return (
    <div className={s.modalBackdrop} role="presentation" onClick={onClose}>
      <div
        className={`${s.modal} ${s.modalWide}`}
        role="dialog"
        aria-labelledby="refollow-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className={s.modalHeader}>
          <h2 id="refollow-modal-title">Schedule follow-up</h2>
          <p className={s.modalCountBadge}>
            <strong>{totalSelected}</strong>
            {' customer'}
            {totalSelected === 1 ? '' : 's'}
            {rows.length < totalSelected ? (
              <span className={s.modalCountNote}>
                {' '}
                (
                {rows.length}
                {' with details loaded'}
                )
              </span>
            ) : null}
          </p>
        </header>

        <div className={s.scheduleModes}>
          <button
            type="button"
            className={`${s.scheduleModeCard}${dateMode === 'same' ? ` ${s.scheduleModeCardActive}` : ''}`}
            onClick={() => setDateMode('same')}
          >
            <span className={s.scheduleModeTitle}>Same for everyone</span>
            <span className={s.scheduleModeDesc}>One date and time for the whole selection</span>
          </button>
          <button
            type="button"
            className={`${s.scheduleModeCard}${dateMode === 'different' ? ` ${s.scheduleModeCardActive}` : ''}`}
            onClick={() => { setDateMode('different'); setPage(1); }}
          >
            <span className={s.scheduleModeTitle}>Per customer</span>
            <span className={s.scheduleModeDesc}>Set date and time on each row</span>
          </button>
        </div>

        {dateMode === 'same' ? (
          <div className={s.sameSchedulePanel}>
            <label className={s.fieldBlock}>
              <span className={s.fieldLabel}>Date (IST)</span>
              <input
                type="date"
                className={s.fieldInput}
                value={sameDate}
                min={todayIstIso()}
                onChange={(e) => setSameDate(e.target.value)}
              />
            </label>
            <label className={s.fieldBlock}>
              <span className={s.fieldLabel}>Time (IST)</span>
              <input
                type="time"
                className={s.fieldInput}
                value={sameTime}
                onChange={(e) => setSameTime(e.target.value)}
              />
            </label>
          </div>
        ) : (
          <div className={s.perCustomerPanel}>
            <div className={s.perRowToolbar}>
              <select
                className={s.pageSizeSelect}
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
              </select>
              <div className={s.perRowPager}>
                <button type="button" className={s.perRowPagerBtn} disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                <span>
                  {safePage}
                  /
                  {totalPages}
                  {' · '}
                  {rows.length}
                  {' total'}
                </span>
                <button type="button" className={s.perRowPagerBtn} disabled={safePage >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            </div>
            <ul className={s.perCustomerList}>
              {pageRows.map((m) => {
                const mid = memberId(m);
                const sch = rowSchedule(mid);
                return (
                  <li key={mid} className={s.perCustomerRow}>
                    <div className={s.perCustomerIdentity}>
                      <strong>{m.name || `Customer #${m.customer_id}`}</strong>
                      <span>{fmtPhone(m)}</span>
                      <span>
                        Customer #
                        {m.customer_id}
                      </span>
                    </div>
                    <div className={s.perRowInputs}>
                      <input
                        type="date"
                        className={s.fieldInput}
                        min={todayIstIso()}
                        value={sch.date}
                        onChange={(e) => patchRow(mid, { date: e.target.value })}
                        aria-label="Date"
                      />
                      <input
                        type="time"
                        className={s.fieldInput}
                        value={sch.time}
                        onChange={(e) => patchRow(mid, { time: e.target.value })}
                        aria-label="Time"
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <footer className={`${s.modalActions} ${s.modalFooter}`}>
          <button type="button" className={s.modalBtnSecondary} onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className={s.modalBtnSecondary} disabled={submitting || !rows.length} onClick={() => submit(false)}>Save draft</button>
          <button type="button" className={s.modalBtnPrimary} disabled={submitting || !rows.length} onClick={() => submit(true)}>
            {submitting ? 'Saving…' : 'Publish'}
          </button>
        </footer>
      </div>
    </div>
  );
}
